/**
 * @file form-object-helper.ts
 * @description Утилиты для работы с объектами форм в Task Manager приложении. Содержит функции для валидации объектов, определения шаблонов, поиска конфигураций сущностей по различным каталогам (catalogs, queues, reports, objects, charts) и создания упрощенных объектов для единообразной обработки данных форм и справочников.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-09-11
 * @dependencies ['@pages/catalogs', '@pages/queues', '@pages/reports', '@pages/objects', '@pages/charts', '@forms/default-object', '@src/types', './table-view']
 * @related-files ['src/forms', 'src/components/form-controls', 'src/pages', 'src/types/mes-object.ts']
 */

import { default as catalogs } from '@pages/catalogs';
import { default as queues } from '@pages/queues';
import { default as reports } from '@pages/reports';
import { default as objects } from '@pages/objects';
import { default as charts } from '@pages/charts';
import { Form } from '@forms/default-object';
import { TBaseObjectDto, GuidUtils } from '@src/types';
import { getDefaultCell } from './table-view';
import { IBaseObjectDTO, ITableItemDTO } from '@src/types/dto';
import { TPageConfig } from '../types/pages';

/**
 * Проверяет, является ли объект валидным
 * @param object - объект для проверки
 * @returns true, если объект содержит валидные id и name, и id не равен VITE_EMPTY_URL
 */
export function getIsObjectValid(object: TBaseObjectDto) {
    return object?.id && object?.name && object.id !== import.meta.env.VITE_EMPTY_URL;
}

/**
 * Проверяет, является ли объект шаблоном
 * @param object - объект для проверки
 * @returns true, если объект является шаблоном (id равен VITE_EMPTY_URL)
 */
export function getIsObjectTemplate(object: TBaseObjectDto) {
    return object?.id && object?.name && object.id === import.meta.env.VITE_EMPTY_URL;
}

/**
 * Получает конфигурацию сущности по типу
 * @param type - тип сущности
 * @returns конфигурация сущности или конфигурация по умолчанию
 * TODO: check by user - нужно уточнить логику поиска по разным каталогам
 */
export function getObjectEntity(type: string): TPageConfig {
    let item: any = catalogs.find((catalog) => catalog.type === type);

    if (!item) {
        item = queues.find((queue) => queue.type === type);
    }

    if (!item) {
        item = reports.find((report) => report.type === type);
    }

    if (!item) {
        item = objects.find((object) => object.type === type);
    }

    if (!item) {
        item = charts.find((chart) => chart.type === type);
    }

    return (
        item || {
            searchModalTitle: 'Выберите из списка',
            type,
            title: 'Список элементов',
            form: Form,
            columns: [
                {
                    id: '$item',
                    header: [{ text: 'Наименование' }],
                    render: (cellValue: any) => getDefaultCell(cellValue)
                }
            ]
        }
    );
}

/**
 * Создает упрощенный объект с базовыми полями
 * @param data - исходные данные для создания объекта
 * @returns объект TBaseObjectDto с полями id, $type и name
 */
export function getSimpleObject(data: IBaseObjectDTO): IBaseObjectDTO {
    return {
        id: data.id || GuidUtils.empty(),
        $type: data.$type || '',
        name: data.name || ''
    };
}

/**
 * Создает массив currentItems для Dictionary компонента
 * @param items - массив объектов для включения в currentItems
 * @returns массив IBaseObjectDTO для передачи в Dictionary currentItems
 */
export function createCurrentItems(items: (IBaseObjectDTO | undefined | null)[]): IBaseObjectDTO[] {
    return items
        .filter((item): item is IBaseObjectDTO =>
            item !== null &&
            item !== undefined &&
            Boolean(item.id) &&
            Boolean(item.$type)
        )
        .map(item => getSimpleObject(item));
}

/**
 * Создает currentItems из одного объекта для Dictionary компонента
 * @param item - объект для включения в currentItems
 * @returns массив с одним элементом IBaseObjectDTO или пустой массив
 */
export function createCurrentItemsFromSingle(item?: IBaseObjectDTO | null): ITableItemDTO[] {
    if (!item || !item.id || !item.$type) {
        return [];
    }
    return [{ ...item, $item: getSimpleObject(item)}];
}
